import { createFileRoute } from "@tanstack/react-router";
import { useAtom } from "jotai";

import { graphql } from "@repo/graphql-types/gql";
import Left from "@repo/icons/default/Left";

import { SlotsFallback } from "@/components/reserved-schedules/reserved-schedule-slot-fallback";
import { ReservedScheduleSlots } from "@/components/reserved-schedules/reserved-schedule-slots";
import { AsyncDataWrapper } from "@/components/ui/async-data-wrapper";
import {
  HeaderButton,
  HeaderRoot,
  HeaderTitle,
} from "@/components/ui/layout/header/header";
import { Page } from "@/components/ui/layout/page/page";
import { useGraphQL } from "@/hooks/use-graphql";
import { reservedScheduleFormAtom } from "@/lib/atoms/reserved-schedule-form-atom";
import { requireChosenModality } from "@/lib/reserved-schedules/require-chosen-modality";
import { useRecordWizardStop } from "@/lib/reserved-schedules/wizard-history";

import type { JSX } from "react";

// biome-ignore lint/plugin/route-file-exports: predates the rule; move it out
export const GetReservedSchedulesSlotsQuery = graphql(/* GraphQL */ `
  query GetReservedSchedulesSlotsQuery(
    $duracao: String!
    $diaDaSemana: Int!
    $codUsuario: Int!
    $codUnidade: Int!
    $codTipoSala: Int!
    $codModalidadePeriodoGarantido: Int!
    $codPeriodoGarantidoRecorrenteFrequencia: Int!
  ) {
    ...ReservedSchedulesSlotsFragment
    ...ReservedScheduleModalitiesFragment
  }
`);

const ReservedSchedulesSlotsPage = (): JSX.Element => {
  const [reservedScheduleFormAtomValue] = useAtom(reservedScheduleFormAtom);
  const { user } = Route.useRouteContext();
  // "Escolher outro horário", no drawer de erro da confirmação, volta para esta entrada.
  useRecordWizardStop("slots");
  const queryResult = useGraphQL(GetReservedSchedulesSlotsQuery, {
    duracao: reservedScheduleFormAtomValue.duration,
    diaDaSemana: reservedScheduleFormAtomValue.dayOfWeek,
    codUsuario: user.codUsuario,
    codUnidade: reservedScheduleFormAtomValue.location,
    codTipoSala: reservedScheduleFormAtomValue.roomType,
    codModalidadePeriodoGarantido: reservedScheduleFormAtomValue.modality,
    codPeriodoGarantidoRecorrenteFrequencia: reservedScheduleFormAtomValue.frequency,
  });

  const { data } = queryResult;

  return (
    <>
      <HeaderRoot>
        <HeaderButton icon={Left} align="start" />
        <HeaderTitle title="Novo período garantido" align="center" />
      </HeaderRoot>
      <Page>
        <AsyncDataWrapper fallback={<SlotsFallback />} {...queryResult}>
          {data ? (
            <ReservedScheduleSlots
              slotsList={data}
              modalitiesFragment={data}
              chosenModality={reservedScheduleFormAtomValue.modality}
              // Não é a flag: é "havia campo de categoria no formulário" (flag **e** duas ou mais
              // opções), que o formulário grava no submit. Assim `/slots` e `/create` decidem pelo
              // mesmo critério, e a flag segue lida num lugar só.
              showsModalityAnnotations={
                reservedScheduleFormAtomValue.modalityChosenExplicitly
              }
            />
          ) : null}
        </AsyncDataWrapper>
      </Page>
    </>
  );
};

export const Route = createFileRoute("/settings/reserved-schedules/create/slots")({
  component: ReservedSchedulesSlotsPage,
  loader: requireChosenModality,
});
